import { Button, Divider, Stack, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import settings from '/@/core/settings';
import { useCheckCuitAvailability } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import type { SidebarGrower } from '../index';
import CuitAlert from './cuit-alert';
import CuitTextField, { validateCuit } from './cuit-text-field';
import GrowerForm from './grower-form';

const AddGrowerSection = ({
    singleGrower = false,
    growers,
    editingGrowerIndex,
    onAddGrower,
    onUpdateGrower,
    defaultRv,
}: {
    growers: SidebarGrower[];
    singleGrower?: boolean;
    editingGrowerIndex: number | null;
    onAddGrower: (grower: SidebarGrower) => void;
    onUpdateGrower: (index: number, grower: SidebarGrower) => void;
    /** Default RV for the grower (Consultant creating from an RV's detail page). */
    defaultRv?: string;
}) => {
    const [cuitDigits, setCuitDigits] = useState('');
    const [cuitError, setCuitError] = useState<string | null>(null);
    const cuitAvailabilityMutation = useCheckCuitAvailability();

    const editingGrower = editingGrowerIndex !== null ? growers[editingGrowerIndex] : null;
    const isEditMode = Boolean(editingGrower?.editable && editingGrower?.id != null);

    const handleValidateCuit = () => {
        if (cuitAvailabilityMutation.data) {
            cuitAvailabilityMutation.reset();
            return;
        }
        setCuitError(null);
        const validationError = validateCuit(cuitDigits);
        if (validationError) {
            setCuitError(validationError);
            return;
        }
        cuitAvailabilityMutation.trigger(cuitDigits).catch(() => {
            const message = _('Error al validar el CUIT. Intenta nuevamente.');
            setCuitError(message);
            enqueueSnackbar(message, { variant: 'error' });
        });
    };

    return (
        <Stack className='mp-mask' width='100%' spacing={3} alignItems='stretch'>
            <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                {_('Agregar nuevo productor')}
            </Typography>
            <Stack alignItems='start' width='100%'>
                <Typography variant='body1' fontWeight='bold' color='primary'>
                    {_('1. Validá el CUIT del productor')}
                </Typography>
                <Typography variant='body2' color='primary.mui.inactive'>
                    {_('Para verificar si ya está creado en {{appName}}.', { appName: settings.app.name })}
                </Typography>
            </Stack>
            <Stack direction='row' spacing={2} width='100%' alignItems='flex-start'>
                <Stack sx={{ flex: 1, minWidth: 0 }}>
                    <CuitTextField
                        value={isEditMode && editingGrower ? editingGrower.cuit : cuitDigits}
                        onChange={(digits) => {
                            setCuitError(null);
                            setCuitDigits(digits);
                        }}
                        disabled={isEditMode || !!cuitAvailabilityMutation.data}
                        error={!!cuitError}
                        helperText={cuitError ?? undefined}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleValidateCuit();
                            }
                        }}
                    />
                </Stack>
                {!isEditMode && (
                    <Button
                        type='button'
                        variant='outlined'
                        color='secondary'
                        sx={{ flex: 'none' }}
                        onClick={handleValidateCuit}
                        loading={cuitAvailabilityMutation.isMutating}
                        disabled={cuitAvailabilityMutation.isMutating}
                    >
                        {cuitAvailabilityMutation.data ? _('Cambiar CUIT') : _('Validar CUIT')}
                    </Button>
                )}
            </Stack>
            {isEditMode && editingGrower ? (
                <GrowerForm
                    singleGrower={singleGrower}
                    cuitDisplay={editingGrower.cuit}
                    mode='edit'
                    editNodeId={editingGrower.id}
                    initialValues={{
                        name: editingGrower.name ?? '',
                        segment: editingGrower.segment === 'premium' ? 'premium' : 'common',
                        advisor: editingGrower.advisor ?? '',
                        location: editingGrower.location ?? '',
                        email: editingGrower.email ?? '',
                        phone: editingGrower.phone ?? '',
                    }}
                    onSubmit={(data) => {
                        if (editingGrowerIndex !== null) {
                            onUpdateGrower(editingGrowerIndex, {
                                ...editingGrower,
                                name: data.name,
                                segment: data.segment,
                                advisor: data.advisor,
                                location: data.location,
                                email: data.email,
                                phone: data.phone,
                            });
                        }
                    }}
                />
            ) : cuitAvailabilityMutation.data?.status === 'available' ? (
                <GrowerForm
                    cuitDisplay={cuitDigits}
                    singleGrower={singleGrower}
                    initialValues={defaultRv ? { rv: defaultRv } : undefined}
                    onSubmit={(data, createdNode) => {
                        onAddGrower({
                            id: createdNode?.id,
                            name: data.name,
                            cuit: cuitDigits,
                            segment: data.segment,
                            advisor: data.advisor,
                            rv: data.rv,
                            editable: true,
                            location: data.location,
                            email: data.email,
                            phone: data.phone,
                        });
                        setCuitDigits('');
                        cuitAvailabilityMutation.reset();
                    }}
                />
            ) : (
                cuitAvailabilityMutation.data && (
                    <CuitAlert
                        cuitAvailabilityOutput={cuitAvailabilityMutation.data}
                        cuit={cuitDigits}
                        onInvitationSent={(invitation) => {
                            if (invitation.invitedBy === 'advisor') {
                                onAddGrower({
                                    rv: invitation.rv.name,
                                    cuit: cuitDigits,
                                    editable: false,
                                });
                            } else {
                                onAddGrower({
                                    advisor: invitation.advisor.name,
                                    cuit: cuitDigits,
                                    editable: false,
                                });
                            }
                            setCuitDigits('');
                            cuitAvailabilityMutation.reset();
                        }}
                    />
                )
            )}
            <Divider />
        </Stack>
    );
};

export default AddGrowerSection;
